import React from 'react';
import { Trophy } from 'lucide-react';
import ChartCard from './ChartCard';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { LIMITER_COLORS } from './RegenBrakingCharts';

const Stat = ({ label, value, sub, testId }) => (
  <div className="min-w-0">
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-lg font-bold text-autoxium-blue break-words" data-testid={testId}>{value}</p>
    {sub && <p className="font-mono text-xs text-muted-foreground">{sub}</p>}
  </div>
);

/** Results Summary — every value read directly off RegenBrakingResponse.summary. */
const RegenSummaryPanel = ({ summary, limiterShares }) => {
  if (!summary) return null;
  const s = summary;
  return (
    <ChartCard icon={Trophy} title="Results Summary" testId="regen-results-summary">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Regen Energy Recovered" value={`${s.regen_recovered_kwh.toFixed(2)} kWh`} sub={`${s.regen_wheel_energy_kwh.toFixed(2)} kWh at the wheels`} testId="summary-regen-recovered" />
        <Stat label="Friction Brake Energy" value={`${s.friction_energy_kwh.toFixed(2)} kWh`} sub="lost as heat" testId="summary-friction" />
        <Stat label="Total Braking Energy" value={`${s.total_braking_energy_kwh.toFixed(2)} kWh`} sub="at the wheels" testId="summary-total-braking" />
        <Stat label="Recovery" value={`${s.recovery_pct.toFixed(1)} %`} sub={`regen DC = ${s.regen_vs_traction_pct.toFixed(1)}% of traction`} testId="summary-recovery" />
        <Stat label="Range With Regen" value={`${s.range_with_regen_km.toFixed(1)} km`} sub={`${s.consumption_with_regen_wh_per_km.toFixed(0)} Wh/km`} testId="summary-range-with" />
        <Stat label="Range Without Regen" value={`${s.range_without_regen_km.toFixed(1)} km`} sub={`${s.consumption_without_regen_wh_per_km.toFixed(0)} Wh/km`} testId="summary-range-without" />
        <Stat label="Range Gain" value={`+${s.range_gain_km.toFixed(1)} km`} sub={`+${s.range_gain_pct.toFixed(1)} %`} testId="summary-range-gain" />
        <Stat label="Battery Temperature" value={`${s.min_battery_temp_c.toFixed(1)}–${s.max_battery_temp_c.toFixed(1)} °C`} sub="min–max over the run" testId="summary-temp" />
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        Energy figures cover the displayed run ({(s.simulated_time_s / 60).toFixed(1)} min,{' '}
        {s.simulated_distance_km.toFixed(1)} km). Ranges are both simulated from Start SoC to Minimum
        SoC on the same pack, looping the cycle — once with the selected blending, once with no
        regen. Pack modelled as {s.pack_series_voltage_v.toFixed(0)} V / {s.pack_capacity_ah.toFixed(1)} Ah;
        motor base speed {s.motor_base_speed_rpm.toFixed(0)} rpm (Peak Power ÷ Peak Torque).
      </p>

      {limiterShares?.length > 0 && (
        <div className="mt-6">
          <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Share of braking time per binding limit ({s.braking_time_s.toFixed(0)} s braking)
          </p>
          <Table data-testid="regen-limiter-table">
            <TableHeader>
              <TableRow>
                <TableHead>Limit</TableHead>
                <TableHead className="text-right">Time (s)</TableHead>
                <TableHead className="text-right">Share</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {limiterShares.map((row) => (
                <TableRow key={row.limiter}>
                  <TableCell className="font-mono text-xs">
                    <span className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle" style={{ backgroundColor: LIMITER_COLORS[row.limiter] }} />
                    {row.label}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{row.time_s.toFixed(0)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{row.pct_of_braking_time.toFixed(1)} %</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </ChartCard>
  );
};

export default RegenSummaryPanel;
